import { apiClient } from './api/client';

interface URLValidationSettings {
    enabled: boolean;
    allowedDomains: string[];
    suspiciousDomains: string[];
    safeContentTypes: string[];
    maxUrlLength: number;
    maxSubdomains: number;
    enforceHttps: boolean;
}

interface EscrowSettings {
    enabled: boolean;
    allowedPlans: string[];
    autoApprovalDays: number;
    maxEscrowAmount: number;
}

interface ApplicationSubmissionSettings {
    submissionAllowed: boolean;
    maxUrlsPerSubmission: number;
    maxDescriptionLength: number;
    allowAttachments: boolean;
    maxAttachmentSizeMB: number;
}

class SettingsService {
    private urlValidationSettings: URLValidationSettings | null = null;
    private escrowSettings: EscrowSettings | null = null;
    private appSubmissionSettings: ApplicationSubmissionSettings | null = null;
    private cacheExpiry = 5 * 60 * 1000; // 5 minutes
    private lastFetch = 0;

    async getURLValidationSettings(): Promise<URLValidationSettings> {
        const now = Date.now();
        
        if (this.urlValidationSettings && (now - this.lastFetch) < this.cacheExpiry) {
            return this.urlValidationSettings;
        }

        try {
            const response = await apiClient.get('/settings/url-validation');
            if (response.data?.data) {
                this.urlValidationSettings = response.data.data;
                this.lastFetch = now;
                return this.urlValidationSettings!;
            }
        } catch (error) {
            console.error('Error fetching URL validation settings:', error);
        }

        // Return defaults if fetch fails
        return this.getDefaultURLValidationSettings();
    }

    async getEscrowSettings(): Promise<EscrowSettings> {
        const now = Date.now();
        
        if (this.escrowSettings && (now - this.lastFetch) < this.cacheExpiry) {
            return this.escrowSettings;
        }

        try {
            const response = await apiClient.get('/settings/escrow');
            if (response.data?.data) {
                this.escrowSettings = response.data.data;
                this.lastFetch = now;
                return this.escrowSettings!;
            }
        } catch (error) {
            console.error('Error fetching escrow settings:', error);
        }

        // Return defaults if fetch fails
        return this.getDefaultEscrowSettings();
    }

    async getApplicationSubmissionSettings(): Promise<ApplicationSubmissionSettings> {
        const now = Date.now();
        
        if (this.appSubmissionSettings && (now - this.lastFetch) < this.cacheExpiry) {
            return this.appSubmissionSettings;
        }

        try {
            const response = await apiClient.get('/settings/application-submission');
            if (response.data?.data) {
                this.appSubmissionSettings = response.data.data;
                this.lastFetch = now;
                return this.appSubmissionSettings!;
            }
        } catch (error) {
            console.error('Error fetching application submission settings:', error);
        }

        // Return defaults if fetch fails
        return this.getDefaultApplicationSubmissionSettings();
    }

    async getAllowedDomains(): Promise<string[]> {
        const settings = await this.getURLValidationSettings();
        return settings.allowedDomains;
    }

    async getSuspiciousDomains(): Promise<string[]> {
        const settings = await this.getURLValidationSettings();
        return settings.suspiciousDomains;
    }

    invalidateCache(): void {
        this.urlValidationSettings = null;
        this.escrowSettings = null;
        this.appSubmissionSettings = null;
        this.lastFetch = 0;
    }

    private getDefaultURLValidationSettings(): URLValidationSettings {
        return {
            enabled: true,
            allowedDomains: [
                'github.com', 'gitlab.com', 'bitbucket.org', 'portfolio.com',
                'behance.net', 'dribbble.com', 'linkedin.com', 'codepen.io',
                'jsfiddle.net', 'stackoverflow.com', 'medium.com', 'dev.to',
                'hashnode.com', 'notion.site', 'figma.com', 'canva.com',
                'adobe.com', 'youtube.com', 'vimeo.com', 'loom.com',
                'drive.google.com', 'docs.google.com', 'dropbox.com',
                'onedrive.live.com', 'wetransfer.com'
            ],
            suspiciousDomains: ['bit.ly', 'tinyurl.com', 'goo.gl', 't.co', 'ow.ly'],
            safeContentTypes: [
                'text/html', 'text/plain', 'application/pdf', 'image/jpeg',
                'image/png', 'image/gif', 'image/webp'
            ],
            maxUrlLength: 2000,
            maxSubdomains: 3,
            enforceHttps: true
        };
    }

    private getDefaultEscrowSettings(): EscrowSettings {
        return {
            enabled: true,
            allowedPlans: ['pro', 'enterprise', 'business'],
            autoApprovalDays: 14,
            maxEscrowAmount: 100000
        };
    }

    private getDefaultApplicationSubmissionSettings(): ApplicationSubmissionSettings {
        return {
            submissionAllowed: true,
            maxUrlsPerSubmission: 10,
            maxDescriptionLength: 2000,
            allowAttachments: true,
            maxAttachmentSizeMB: 25
        };
    }
}

export const settingsService = new SettingsService();
